import type { OverlayParams } from './types';
import { TEMPLATE_SIZE, TEMPLATE_BOUNDS, getAdaptiveFontSize, LOGO_BOUNDS, DEFAULT_LOGO_SIZE } from '../video';
import { fillTextSplit } from '../text/fillTextSplit';
import { colorWithOpacity } from '../text/colorWithOpacity';

export const LOGO_BOUNDS_WORKER = LOGO_BOUNDS;


function getLogoBounds(
  logoPosition: string | undefined,
  canvasWidth: number,
  canvasHeight: number,
  size: number,
): { x: number; y: number; width: number; height: number } {
  const { x: xPad, y: yTop } = LOGO_BOUNDS;
  switch (logoPosition) {
    case 'top-center':
      return { x: (canvasWidth - size) / 2, y: yTop, width: size, height: size };
    case 'top-right':
      return { x: canvasWidth - size - xPad, y: yTop, width: size, height: size };
    case 'bottom-left':
      return { x: xPad, y: canvasHeight - size - xPad, width: size, height: size };
    case 'bottom-center':
      return { x: (canvasWidth - size) / 2, y: canvasHeight - size - xPad, width: size, height: size };
    case 'bottom-right':
      return { x: canvasWidth - size - xPad, y: canvasHeight - size - xPad, width: size, height: size };
    default:
      return { x: xPad, y: yTop, width: size, height: size };
  }
}

export function shouldDrawText(
  textDuration: number | 'full',
  currentTimeSec: number,
): boolean {
  if (textDuration === 'full') return true;
  return currentTimeSec <= (textDuration as number);
}

export function drawOverlay(
  ctx: OffscreenCanvasRenderingContext2D,
  canvas: OffscreenCanvas,
  currentTimeSec: number,
  overlay: OverlayParams,
  logoBitmap: ImageBitmap | null,
  frameBitmap: ImageBitmap | null = null,
): void {
  // Каркас (PNG-оверлей) поверх видео, под лого и текстом.
  if (frameBitmap) {
    ctx.drawImage(frameBitmap, 0, 0, canvas.width, canvas.height);
  }
  if (logoBitmap) {
    const size = overlay.logoSize && overlay.logoSize > 0 ? overlay.logoSize : DEFAULT_LOGO_SIZE;
    const lb = getLogoBounds(overlay.logoPosition, canvas.width, canvas.height, size);
    ctx.drawImage(logoBitmap, lb.x, lb.y, lb.width, lb.height);
  }

  if (!overlay.text || !shouldDrawText(overlay.textDuration ?? 'full', currentTimeSec)) {
    return;
  }

  const scaleX = TEMPLATE_SIZE.width / overlay.textPosition.previewWidth;
  const textX = overlay.textPosition.x * scaleX;
  // Per-side padding (preview-space px → render-space ×scaleX). New fields fall
  // back to the legacy single textBoxPadding, then 12. Bottom mirrors top.
  const padTop = (overlay.textBoxPaddingTop ?? overlay.textBoxPadding ?? 12) * scaleX;
  const padLeft = (overlay.textBoxPaddingLeft ?? overlay.textBoxPadding ?? 12) * scaleX;
  const padRight = (overlay.textBoxPaddingRight ?? overlay.textBoxPadding ?? 12) * scaleX;
  const fontSizePreview = overlay.fontSize ?? getAdaptiveFontSize(overlay.text.length);
  const scaledFontSize = fontSizePreview * scaleX;
  const lineHeight = scaledFontSize * (overlay.lineSpacing && overlay.lineSpacing > 0 ? overlay.lineSpacing : 1.0);
  const displayText = overlay.textUppercase ? overlay.text.toUpperCase() : overlay.text;

  const _fv = overlay.fontVariationSettings;
  const _weight = _fv?.wght ?? 'normal';
  const _style =
    _fv?.ital && _fv.ital > 0.5
      ? 'italic'
      : _fv?.slnt && _fv.slnt !== 0
        ? `oblique ${Math.abs(_fv.slnt)}deg`
        : 'normal';
  // Кавычки обязательны — см. wrapTextLines/calculateFontSize: имена кастомных
  // шрифтов содержат точки/запятые, без кавычек canvas молча остаётся на 10px.
  ctx.font = `${_style} ${_weight} ${scaledFontSize}px "${overlay.fontFamily || 'Arial'}", sans-serif`;
  if (_fv && Object.keys(_fv).length > 0) {
    const _variationCSS = Object.entries(_fv)
      .map(([axis, val]) => `'${axis}' ${val}`)
      .join(', ');
    try {
      (ctx as any).fontVariationSettings = _variationCSS;
    } catch {
      // fallback: weight/style already encoded in ctx.font above
    }
  }

  const FULL_WIDTH = TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left;
  const NOTCH_WIDTH = TEMPLATE_BOUNDS.rightCornerLeft - TEMPLATE_BOUNDS.left;

  const wrapLines = (availableWidth: number): string[] => {
    const words = displayText.split(' ');
    const out: string[] = [];
    let line = '';
    const maxLineWidth = availableWidth - padLeft - padRight;
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      if (ctx.measureText(testLine).width > maxLineWidth && i > 0) {
        out.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    if (line) out.push(line.trim());
    return out;
  };
  const boxHeightFor = (lineCount: number) => lineCount * lineHeight + padTop * 2;

  const bottom = overlay.textPosition.bottom;
  let lines: string[];
  let textY: number;

  if (overlay.lines) {
    // Preferred: browser already wrapped. Draw verbatim, no measuring for layout.
    lines = overlay.lines;
    textY =
      typeof bottom === 'number'
        ? TEMPLATE_SIZE.height - bottom - boxHeightFor(lines.length)
        : overlay.textPosition.y * scaleX;
  } else if (typeof bottom === 'number') {
    // Legacy bottom-anchor canvas wrap.
    const bottomEdgeY = TEMPLATE_SIZE.height - bottom;
    let availableWidth = FULL_WIDTH;
    lines = wrapLines(availableWidth);
    textY = bottomEdgeY - boxHeightFor(lines.length);
    const refined = textY < TEMPLATE_BOUNDS.rightCornerTop ? FULL_WIDTH : NOTCH_WIDTH;
    if (refined !== availableWidth) {
      availableWidth = refined;
      lines = wrapLines(availableWidth);
      textY = bottomEdgeY - boxHeightFor(lines.length);
    }
  } else {
    // Legacy top-anchor canvas wrap.
    textY = overlay.textPosition.y * scaleX;
    const availableWidth = textY < TEMPLATE_BOUNDS.rightCornerTop ? FULL_WIDTH : NOTCH_WIDTH;
    lines = wrapLines(availableWidth);
  }

  let actualTextBoxWidth = 0;
  for (const l of lines) {
    actualTextBoxWidth = Math.max(actualTextBoxWidth, ctx.measureText(l).width);
  }
  const textBoxWidth = actualTextBoxWidth + padLeft + padRight;
  const textBoxHeight = boxHeightFor(lines.length);

  // Пресет фон-бокса. square-flush: бокс УДЛИНЯЕТСЯ влево до края кадра (x=0).
  // square-flush-right: УДЛИНЯЕТСЯ вправо до края кадра (canvas.width). В обоих
  // ТЕКСТ остаётся на месте. square/square-flush*: острые углы.
  const boxStyle = overlay.textBoxStyle ?? 'rounded';
  // Center-anchor: box centered at hCenterX*width, clamped to safe-area left. Overrides
  // flush (flush = edge-anchored, contradicts centering).
  const hCenter = overlay.hCenterX != null;
  const isFlush = !hCenter && boxStyle === 'square-flush';
  const isFlushRight = !hCenter && boxStyle === 'square-flush-right';
  const centeredLeft = hCenter
    ? Math.max(TEMPLATE_BOUNDS.left, overlay.hCenterX! * canvas.width - textBoxWidth / 2)
    : 0;
  const boxDrawX = hCenter ? centeredLeft : isFlush ? 0 : textX;
  const boxDrawWidth = hCenter
    ? textBoxWidth
    : isFlush
      ? textX + textBoxWidth
      : isFlushRight
        ? canvas.width - textX
        : textBoxWidth;

  // Выравнивание строк внутри текст-блока. При center-anchor — принудительно по центру
  // относительно левого края бокса (boxDrawX + padLeft); иначе legacy от textX.
  const align = overlay.textAlign ?? 'left';
  const baseLeft = hCenter ? boxDrawX : textX;
  const lineX = (line: string): number => {
    const base = baseLeft + padLeft;
    if (align === 'left') return base;
    const w = ctx.measureText(line).width;
    return align === 'center' ? base + (actualTextBoxWidth - w) / 2 : base + (actualTextBoxWidth - w);
  };

  // Граница первого цвета может пройти ПОСРЕДИ строки (до двоеточия), поэтому
  // строку рисуем не одним fillText, а двумя кусками. lineX уже даёт левый край
  // строки при любом выравнивании — здесь textAlign всегда 'left'.
  const primaryLines = overlay.primaryLines ?? 1;
  const splitChars = overlay.splitChars ?? 0;
  const paintLine = (i: number, lineY: number) => {
    const line = lines[i];
    if (!overlay.useTwoColors || !overlay.textColorSecondary) {
      ctx.fillStyle = overlay.textColor || 'white';
      ctx.fillText(line, lineX(line), lineY);
      return;
    }
    const chars = i < primaryLines ? line.length : i === primaryLines ? splitChars : 0;
    fillTextSplit(ctx, line, lineX(line), lineY, chars, overlay.textColor || 'white', overlay.textColorSecondary);
  };

  // Наклон всей плашки (бокс + soft-shadow + outline + текст) вокруг её центра.
  // rotate(0) = identity, поэтому применяем безусловно. restore() ниже, после
  // основного текст-цикла.
  const boxAngle = ((overlay.textBoxAngle ?? 0) * Math.PI) / 180;
  const boxCx = boxDrawX + boxDrawWidth / 2;
  const boxCy = textY + textBoxHeight / 2;
  ctx.save();
  ctx.translate(boxCx, boxCy);
  ctx.rotate(boxAngle);
  ctx.translate(-boxCx, -boxCy);

  const alpha = overlay.backgroundAlpha ?? 1;
  if (alpha > 0) {
    const hex = (overlay.backgroundColor || '#000000').replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
    // Радиус = % от ПОЛОВИНЫ высоты бокса (0..100). Округлость не зависит от
    // отступа/размера: cr и высота растут вместе, отношение постоянно. 100% = капсула.
    // Дефолт по стилю: rounded → 30%, flush/square → 0 (острые), когда не задан.
    const defaultRadiusPct = boxStyle === 'rounded' ? 30 : 0;
    const radiusPct = Math.max(0, Math.min(100, overlay.textBoxRadius ?? defaultRadiusPct));
    const cr = (radiusPct / 100) * (textBoxHeight / 2);
    // flush упирается одним краем в кадр — тот край острый, скругляем только
    // внутренний. roundRect: [tl, tr, br, bl]. flush-left (тянется влево) →
    // скруглить правые; flush-right (тянется вправо) → скруглить левые.
    const radii = isFlush ? [0, cr, cr, 0] : isFlushRight ? [cr, 0, 0, cr] : cr;
    ctx.beginPath();
    ctx.roundRect(boxDrawX, textY, boxDrawWidth, textBoxHeight, radii);
    ctx.fill();
  }

  // Soft drop-shadow pre-pass: lays text + its diffuse shadow behind the
  // crisp text drawn by the main loop below. Independent of the outline
  // (textShadowEnabled); the canvas holds one shadow at a time, so this runs
  // first and resets before the outline pass.
  if (overlay.textSoftShadowEnabled) {
    ctx.shadowColor = colorWithOpacity(overlay.textSoftShadowColor || '#000000', overlay.textSoftShadowOpacity ?? 100);
    ctx.shadowBlur = (overlay.textSoftShadowBlur ?? 8) * scaleX;
    ctx.shadowOffsetX = (overlay.textSoftShadowOffsetX ?? 2) * scaleX;
    ctx.shadowOffsetY = (overlay.textSoftShadowOffsetY ?? 4) * scaleX;
    ctx.textBaseline = 'top';
    // Большое размытие рассеивает тень → она бледнеет. Перерисовываем затенённый
    // текст несколько раз (альфа аккумулируется) — тень остаётся насыщенной.
    // Чем больше blur, тем больше проходов. Малый blur = 1 проход (как раньше).
    const softPasses = Math.min(3, 1 + Math.round((overlay.textSoftShadowBlur ?? 8) / 14));
    for (let p = 0; p < softPasses; p++) {
      let sy = textY + padTop;
      for (let i = 0; i < lines.length; i++) {
        paintLine(i, sy);
        sy += lineHeight;
      }
    }
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  if (overlay.textShadowEnabled) {
    ctx.strokeStyle = colorWithOpacity(overlay.textShadowColor || '#000000', overlay.textShadowOpacity ?? 100);
    // The WYSIWYG preview draws the outline via CSS -webkit-text-stroke, which paints
    // ~2x wider than a centered canvas strokeText of the same nominal width. Double the
    // width here so the burned outline matches the preview. Crisp — no blur halo.
    ctx.lineWidth = (overlay.textShadowWidth ?? 4) * 2 * scaleX;
    ctx.lineJoin = 'round';
  }

  ctx.textBaseline = 'top';
  let y = textY + padTop;
  for (let i = 0; i < lines.length; i++) {
    if (overlay.textShadowEnabled) ctx.strokeText(lines[i], lineX(lines[i]), y);
    paintLine(i, y);
    y += lineHeight;
  }

  // Закрываем наклон-обёртку (boxAngle save выше, перед заливкой бокса).
  ctx.restore();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}
